import { ApproveReturnArgs } from '@c/modals/ApproveReturnModal';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { db } from '@util/firebase';
import { createChat, sendMessageToChat } from '@util/firestore/messages';
import {
  ReturnsCursor,
  addReturnNote,
  approveReturnWithHigherRateConfirm,
  cancelReturn,
  completeReturn,
  convertPartialRefundToReturn,
  denyReturn,
  getReturnById,
  getReturnsByOrderId,
  getReturnsByUser,
  getReturnsPage,
  getWarehouseReturns,
  initiateRefund,
  reopenReturn,
  sendPartialRefundDeclinedMessage,
} from '@util/firestore/returns/returns.service';
import { ADMIN_UID, hasSupportPermission } from '@util/support';
import { useAuth } from 'context/AuthContext';
import { doc, updateDoc } from 'firebase/firestore';
import {
  ChatDocument,
  ChatMessageInput,
  chatMessagesSchema,
} from 'models/chat';
import { OrderDocument } from 'models/order';
import { ReturnDocument, ReturnState } from 'models/returns';
import { useMemo } from 'react';

/**
 * Outcome of denying a group of returns. `v2_denyReturn` takes one id at a
 * time, so a group deny can land partway — the caller needs to know which
 * returns actually moved.
 */
export type DenyReturnsResult = {
  chat_id: string | null;
  denied: string[];
  failed: { id: string; message: string }[];
};

export function useReturns({
  orderId,
  uid,
  field,
  returnId,
  state,
  states,
  warehouse,
  seller_returns_only,
  onMarkedAsReceived,
  onApproved,
  onDenied,
  onDeniedMany,
}: {
  orderId?: string;
  uid?: string;
  field?: 'buyer_id' | 'seller_id';
  returnId?: string;
  state?: ReturnState;
  /**
   * Which states the support lists want, and the switch that turns the list on
   * at all. Without it nothing is read: every caller of this hook used to pull
   * the whole returns collection just by being on a support account, whether or
   * not it ever looked at `data`.
   */
  states?: ReturnState[];
  /** Opt in to the warehouse list, for the same reason. */
  warehouse?: boolean;
  seller_returns_only?: boolean;
  onMarkedAsReceived?: (success: boolean) => void;
  onApproved?: (success: boolean) => void;
  onDenied?: (chat_id: string | null) => void;
  onDeniedMany?: (result: DenyReturnsResult) => void;
} = {}) {
  const queryClient = useQueryClient();
  const { userDoc } = useAuth();
  const enabled = !userDoc?.uid
    ? false
    : hasSupportPermission(userDoc.uid) ||
      userDoc.uid === 'qlcEanuNfBZOpI7CmwgLN0QKPlo1'; // socal,
  const {
    data: returnPages,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['returns', states, userDoc?.uid],
    queryFn: ({ pageParam }) =>
      getReturnsPage({ states: states ?? [], cursor: pageParam }),
    initialPageParam: null as ReturnsCursor,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.cursor : undefined,
    enabled: enabled && !!states?.length,
  });

  /**
   * Every page loaded so far, flattened. Memoised because `useReactTable`
   * rebuilds its row model whenever `data` changes identity.
   */
  const data = useMemo(
    () => returnPages?.pages.flatMap((page) => page.returns),
    [returnPages]
  );

  const { data: returnsByOrder } = useQuery({
    queryKey: ['returnByOrder', orderId, uid, field, seller_returns_only],
    queryFn: async () => {
      if (!orderId || !uid) return null;
      const returnRequests = await getReturnsByOrderId(
        orderId,
        uid,
        field ?? 'buyer_id',
        seller_returns_only
      );
      return returnRequests;
    },
    enabled: !!orderId && !!uid,
  });

  const { data: returnsByUser, isLoading: isLoadingReturnsByUser } = useQuery({
    queryKey: ['returnsByUser', uid, state, field],
    queryFn: async () => {
      if (!uid || !field) return null;
      const returns = await getReturnsByUser(uid, field, state);
      return returns;
    },
    enabled: !!uid && !!field,
    throwOnError: true,
  });

  const { data: returnById, isLoading: isLoadingReturnById } = useQuery({
    queryKey: ['returnById', returnId],
    queryFn: async () => {
      if (!returnId) return null;
      const returnRequest = await getReturnById(returnId);
      return returnRequest;
    },
    enabled: !!returnId,
  });

  const { data: warehouseReturns } = useQuery({
    queryKey: ['warehouseReturns', state, userDoc?.uid],
    queryFn: async () => {
      const returns = await getWarehouseReturns(state);
      return returns;
    },
    enabled: enabled && !!warehouse,
  });

  const markAsReceivedMutation = useMutation({
    mutationFn: async ({
      id,
      received_by_warehouse,
      warehouse_comments,
      images,
      is_warehouse_return,
      order_id,
      chat,
    }: Pick<
      ReturnDocument,
      | 'id'
      | 'received_by_warehouse'
      | 'warehouse_comments'
      | 'images'
      | 'is_warehouse_return'
      | 'order_id'
    > & { chat?: ChatDocument }) => {
      await updateDoc(
        doc(db, 'returns', id),
        received_by_warehouse === 'needs_attention'
          ? { received_by_warehouse, warehouse_comments, images }
          : { received_by_warehouse }
      );
      if (
        received_by_warehouse === 'needs_attention' &&
        !is_warehouse_return &&
        userDoc?.uid
      ) {
        const messages = [];
        const initialMessage: ChatMessageInput = {
          created_at: Date.now(),
          content: `Return arrived not as described`,
          uid: userDoc.uid,
        };
        if (warehouse_comments) {
          initialMessage.content += `: ${warehouse_comments}`;
        }
        const parsedInitialMessage = chatMessagesSchema.parse(initialMessage);
        messages.push(parsedInitialMessage);

        if (images) {
          const imageMessages = images.map((image) => {
            const message: ChatMessageInput = {
              created_at: Date.now(),
              content: image.thumb,
              is_img: true,
              uid: userDoc.uid,
            };
            const parsedMessage = chatMessagesSchema.parse(message);
            return parsedMessage;
          });
          messages.push(...imageMessages);
        }
        if (chat) {
          await sendMessageToChat(messages, chat, userDoc);
        } else {
          await createChat({
            created_at: Date.now(),
            buyer_id: ADMIN_UID,
            seller_id: userDoc.uid,
            messages,
            last_time: Date.now(),
            id: '',
            uids: [ADMIN_UID, userDoc.uid],
            order_id,
            unread: {
              [ADMIN_UID]: true,
            },
          });
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['returns'] });
      onMarkedAsReceived?.(true);
    },
    onError: (error) => {
      alert(error.message);
    },
  });

  const approveReturnMutation = useMutation({
    mutationFn: async (args: ApproveReturnArgs) => {
      const result = await approveReturnWithHigherRateConfirm(args);
      return { id: args.id, approved: result !== null };
    },
    onSuccess: async ({ approved }) => {
      if (!approved) return;
      alert('Return Approved');
      onApproved?.(true);
      // An approval can touch every return grouped onto the same label, so
      // refresh the lists that show them alongside this one.
      queryClient.invalidateQueries({ queryKey: ['returns'] });
      queryClient.invalidateQueries({ queryKey: ['returnByOrder'] });
      queryClient.invalidateQueries({ queryKey: ['returnsByUser'] });
      queryClient.invalidateQueries({ queryKey: ['returnById'] });
    },
    onError: (error) => {
      alert(error.message);
    },
  });

  const denyReturnMutation = useMutation({
    mutationFn: async ({
      id,
      arrived_as_described,
      not_responding,
      partial_refund_declined,
      returnDoc,
    }: {
      id: string;
      arrived_as_described?: boolean;
      not_responding?: boolean;
      // no flag on the callable for this one, so the message is posted here
      partial_refund_declined?: boolean;
      returnDoc?: ReturnDocument;
    }) => {
      const chat_id = await denyReturn({
        id,
        arrived_as_described,
        not_responding,
      });
      if (partial_refund_declined && returnDoc) {
        await sendPartialRefundDeclinedMessage({ returnDoc, chatId: chat_id });
      }
      return chat_id;
    },
    onSuccess: (chat_id: string | null) => {
      onDenied?.(chat_id);
      queryClient.invalidateQueries({ queryKey: ['returns'] });
    },
    onError: (error) => {
      alert(error.message);
    },
  });

  const denyReturnsMutation = useMutation({
    mutationFn: async ({
      ids,
      arrived_as_described,
      not_responding,
      partial_refund_declined,
      returns,
    }: {
      ids: string[];
      arrived_as_described?: boolean;
      not_responding?: boolean;
      partial_refund_declined?: boolean;
      returns?: ReturnDocument[];
    }): Promise<DenyReturnsResult> => {
      const denied: string[] = [];
      const failed: DenyReturnsResult['failed'] = [];
      let chat_id: string | null = null;
      // One at a time, and a rejection is collected rather than thrown: the
      // callable settles each return on its own, so stopping at the first
      // error would hide which of the rest went through.
      for (const id of ids) {
        try {
          const result = await denyReturn({
            id,
            arrived_as_described,
            not_responding,
          });
          denied.push(id);
          chat_id ??= result ?? null;
        } catch (error) {
          failed.push({
            id,
            message: error instanceof Error ? error.message : 'Unknown error',
          });
        }
      }
      // One message for the group rather than one per return: they share a
      // chat, so a note per item would repeat itself down the thread.
      const partialDoc = returns?.find(
        (ret) => denied.includes(ret.id) && ret.resolution === 'partial'
      );
      if (partial_refund_declined && partialDoc) {
        await sendPartialRefundDeclinedMessage({
          returnDoc: partialDoc,
          chatId: chat_id,
        });
      }
      return { chat_id, denied, failed };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['returns'] });
      queryClient.invalidateQueries({ queryKey: ['returnByOrder'] });
      queryClient.invalidateQueries({ queryKey: ['returnsByUser'] });
      queryClient.invalidateQueries({ queryKey: ['returnById'] });
      onDeniedMany?.(result);
    },
    onError: (error) => {
      alert(error.message);
    },
  });

  const convertPartialRefundMutation = useMutation({
    mutationFn: async ({ returnDoc }: { returnDoc: ReturnDocument }) => {
      await convertPartialRefundToReturn({ returnDoc, uid: userDoc?.uid });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['returns'] });
      queryClient.invalidateQueries({ queryKey: ['returnByOrder'] });
      queryClient.invalidateQueries({ queryKey: ['returnsByUser'] });
      queryClient.invalidateQueries({ queryKey: ['returnById'] });
    },
    onError: (error) => {
      alert(error.message);
    },
  });

  const initiateRefundMutation = useMutation({
    mutationFn: async ({
      order,
      returnDoc,
      amount,
    }: {
      order: OrderDocument;
      returnDoc: ReturnDocument;
      amount: number;
    }) => {
      return await initiateRefund({ order, returnDoc, amount });
    },
    onSuccess: async ({ id, amount }) => {
      alert('Refund Initiated');
      queryClient.invalidateQueries({ queryKey: ['returns'] });
      await addReturnNote({
        id,
        note: `Refund initiated by admin for ${amount}`,
      });
    },
    onError: (error) => {
      alert(error.message);
    },
  });

  const completeReturnMutation = useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      await completeReturn({ id });
    },
    onSuccess: () => {
      alert('Return Completed');
      queryClient.invalidateQueries({ queryKey: ['returns'] });
    },
    onError: (error) => {
      alert(error.message);
    },
  });

  const cancelReturnMutation = useMutation({
    mutationFn: async ({ returnDoc }: { returnDoc: ReturnDocument }) => {
      await cancelReturn({ returnDoc });
    },
    onSuccess: () => {
      alert('Return Cancelled');
      queryClient.invalidateQueries({ queryKey: ['returns'] });
    },
  });

  const reopenReturnMutation = useMutation({
    mutationFn: async ({ returnDoc }: { returnDoc: ReturnDocument }) => {
      await reopenReturn({ returnDoc });
    },
    onSuccess: () => {
      alert('Return Reopened');
      queryClient.invalidateQueries({ queryKey: ['returns'] });
    },
    onError: (error) => {
      alert(error.message);
    },
  });

  return {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    approveReturnMutation,
    denyReturnMutation,
    denyReturnsMutation,
    convertPartialRefundMutation,
    initiateRefundMutation,
    returnsByOrder,
    returnsByUser,
    returnById,
    isLoadingReturnsByUser,
    isLoadingReturnById,
    completeReturnMutation,
    warehouseReturns,
    markAsReceivedMutation,
    cancelReturnMutation,
    reopenReturnMutation,
  };
}
